import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { createOrg } from "shared";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAppForm } from "@/hooks/useCreateForm";
import { queryClient } from "@/lib/trpcClient";

export const Route = createFileRoute("/_authed/create-organization")({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(
      context.trpc.auth.session.queryOptions(),
    );

    if (session.org && session.org?.status !== "approved") {
      throw redirect({
        to: "/onboard",
      });
    }
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc } = Route.useRouteContext();
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation(
    trpc.org.create.mutationOptions({
      onError: (error) => {
        toast.error("Error", {
          description: error.message,
        });
      },
      onSuccess: async (data) => {
        await queryClient.refetchQueries(trpc.auth.session.queryOptions());
        await queryClient.refetchQueries(trpc.org.list.queryOptions());
        toast.success(`Created organization: ${data.name}`, {
          description: "Complete the onboarding to begin issuing cards",
        });

        setTimeout(
          () =>
            navigate({
              to: "/onboard",
            }),
          300,
        );
      },
    }),
  );

  const form = useAppForm({
    defaultValues: {
      address: {
        addressLine1: "156 2nd St",
        addressLine2: null as string | null,
        city: "San Francisco",
        state: "CA",
        zipcode: "94104",
      },
      dbaName: "" as string | null,
      ein: "",
      name: "",
    },
    onSubmit: ({ value }) => {
      // @ts-ignore
      mutate({ ...value });
    },
    validators: {
      onSubmit: createOrg,
    },
  });

  return (
    <div className="container mx-auto max-w-3xl px-4 py-10">
      <h1 className="mb-6 font-bold text-3xl">Create a New Organization</h1>

      <div className="space-y-8">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>
                Enter the basic information about your organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <form.AppField
                  children={(field) => {
                    return (
                      <field.TextField
                        description="This must exactly match the name listed on your IRS documents including capitalization and punctuation."
                        descriptionStyle="below"
                        label="Organization Legal Name"
                        required={true}
                      />
                    );
                  }}
                  name="name"
                />
                <form.AppField
                  children={(field) => {
                    return (
                      <field.TextField
                        label="Nonprofit Employer Identification Number (EIN)"
                        maxLength={9}
                        required={true}
                        type="number"
                      />
                    );
                  }}
                  name="ein"
                />
                <form.AppField
                  children={(field) => {
                    return (
                      <field.TextField label="Doing Business As (DBA) Name" />
                    );
                  }}
                  name="dbaName"
                />
              </div>
            </CardContent>
            <form.Subscribe
              selector={(state) => state.values.address.state}
              children={() => {
                return (
                  <CardFooter className="flex justify-end">
                    <Button disabled={isPending} type="submit">
                      {isPending && <Loader2 className="animate-spin" />}
                      Begin Onboarding
                    </Button>
                  </CardFooter>
                );
              }}
            />
          </Card>
        </form>
      </div>
    </div>
  );
}
